import React, { useState } from 'react';
import { Modal, Table, Typography, Space, Button, Spin, Tag, Row, Col, Card, Dropdown, message } from 'antd';
import { DownloadOutlined, FileExcelOutlined, FileTextOutlined, ShopOutlined, UserOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth } from '../../../aws/AuthContext';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
// import { exportarExcelEstilizado, exportarCSVPlano } from '../../../utils/exportUtils'; // Ideal abstraer esto

const { Title, Text } = Typography;

const ModalAnalisisVendedor = ({ visible, onClose, empleado, desde, hasta }) => {
    const { getJwtToken } = useAuth();
    const API_URL = process.env.REACT_APP_API_URL;
    const [tipoExportacion, setTipoExportacion] = useState('completo');

    // Fetch solo de los detalles de este vendedor
    const { data: detallesVendedor, isLoading } = useQuery({
        queryKey: ['analisisProfundo', empleado?.id, desde, hasta],
        queryFn: async () => {
            const token = await getJwtToken();
            const response = await axios.get(`${API_URL}/v1-informes-vendedor-detalle`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { fecha_inicio: desde, fecha_fin: hasta, usuario_id: empleado.id }
            });
            return response.data; // Retorna un array agrupado por cliente
        },
        enabled: !!empleado
    });

    const itemsExport = [
        {
            key: 'xlsx',
            label: 'Reporte Ejecutivo (Con Diseño .xlsx)',
            icon: <FileExcelOutlined style={{ color: '#10b981' }} />,
            onClick: () => handleExport('xlsx')
        },
        {
            key: 'csv',
            label: 'Datos Crudos (Para PowerBI/Tablas .csv)',
            icon: <FileTextOutlined style={{ color: '#64748b' }} />,
            onClick: () => handleExport('csv')
        }
    ];

    const handleExport = async (formato) => {
        if (!detallesVendedor || detallesVendedor.length === 0) {
            return message.warning("No hay datos para exportar en este momento.");
        }

        message.loading({ content: `Generando ${formato.toUpperCase()}...`, key: 'exporting' });

        // 1. Aplanar los datos
        const datosPlanos = [];
        detallesVendedor.forEach(cliente => {
            cliente.facturas.forEach(factura => {
                if (factura.productos && factura.productos.length > 0) {
                    factura.productos.forEach(prod => {
                        datosPlanos.push({
                            cliente: cliente.nombre_cliente,
                            negocio: cliente.nombre_comercial || 'N/A',
                            documento: cliente.documento_cliente,
                            tipo_cliente: cliente.tipo_cliente || 'N/A',
                            telefono: cliente.telefono || 'N/A',
                            ciudad: cliente.ciudad || 'N/A',
                            barrio: cliente.barrio || 'N/A',
                            factura: factura.numero_factura,
                            caja: factura.sesion_caja_id,
                            fecha: factura.fecha,
                            metodo_pago: factura.metodo_pago || 'N/A',
                            producto: prod.nombre,
                            cantidad: prod.cantidad,
                            precio: prod.precio_unitario,
                            subtotal: prod.subtotal
                        });
                    });
                } else {
                    datosPlanos.push({
                        cliente: cliente.nombre_cliente,
                        negocio: cliente.nombre_comercial || 'N/A',
                        documento: cliente.documento_cliente,
                        tipo_cliente: cliente.tipo_cliente || 'N/A',
                        telefono: cliente.telefono || 'N/A',
                        ciudad: cliente.ciudad || 'N/A',
                        barrio: cliente.barrio || 'N/A',
                        factura: factura.numero_factura,
                        caja: factura.sesion_caja_id,
                        fecha: factura.fecha,
                        metodo_pago: factura.metodo_pago || 'N/A',
                        producto: 'Venta General (Sin detalle)',
                        cantidad: 1,
                        precio: factura.total,
                        subtotal: factura.total
                    });
                }
            });
        });

        // 2. Generar archivo
        if (formato === 'xlsx') {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Análisis Profundo');

            worksheet.columns = [
                { header: 'Cliente', key: 'cliente', width: 25 },
                { header: 'Negocio', key: 'negocio', width: 20 },
                { header: 'Documento', key: 'documento', width: 15 },
                { header: 'Tipo Cliente', key: 'tipo_cliente', width: 18 },
                { header: 'Teléfono', key: 'telefono', width: 15 },
                { header: 'Ciudad', key: 'ciudad', width: 15 },
                { header: 'Barrio', key: 'barrio', width: 15 },
                { header: 'Factura', key: 'factura', width: 15 },
                { header: 'ID Caja', key: 'caja', width: 10 },
                { header: 'Fecha', key: 'fecha', width: 22 },
                { header: 'Método Pago', key: 'metodo_pago', width: 15 },
                { header: 'Producto', key: 'producto', width: 35 },
                { header: 'Cant.', key: 'cantidad', width: 10 },
                { header: 'Precio Un.', key: 'precio', width: 15 },
                { header: 'Subtotal', key: 'subtotal', width: 15 }
            ];

            worksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
            worksheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF22BABB' } };

            datosPlanos.forEach(row => {
                const excelRow = worksheet.addRow(row);
                excelRow.getCell('precio').numFmt = '"$"#,##0.00';
                excelRow.getCell('subtotal').numFmt = '"$"#,##0.00';
            });

            const buffer = await workbook.xlsx.writeBuffer();
            saveAs(new Blob([buffer]), `Analisis_Ventas_${empleado.nombre_completo.replace(/\s+/g, '_')}_${dayjs().format('YYYYMMDD')}.xlsx`);
            message.success({ content: 'Excel descargado exitosamente', key: 'exporting' });

        } else if (formato === 'csv') {
            const headers = "Cliente,Negocio,Documento,Tipo Cliente,Teléfono,Ciudad,Barrio,Factura,ID Caja,Fecha,Método Pago,Producto,Cantidad,Precio Unitario,Subtotal\n";
            const csvRows = datosPlanos.map(r =>
                `"${r.cliente}","${r.negocio}","${r.documento}","${r.tipo_cliente}","${r.telefono}","${r.ciudad}","${r.barrio}","${r.factura}","${r.caja}","${r.fecha}","${r.metodo_pago}","${r.producto}",${r.cantidad},${r.precio},${r.subtotal}`
            ).join("\n");

            const blob = new Blob(["\uFEFF" + headers + csvRows], { type: 'text/csv;charset=utf-8;' });
            saveAs(blob, `Analisis_Ventas_${empleado.nombre_completo.replace(/\s+/g, '_')}.csv`);
            message.success({ content: 'CSV descargado exitosamente', key: 'exporting' });
        }
    };

    // --- Definición de Tablas Anidadas ---

    // Nivel 3: Productos
    const expandedFactura = (factura) => {
        const columns = [
            { title: 'Producto', dataIndex: 'nombre', key: 'nombre' },
            { title: 'Cant.', dataIndex: 'cantidad', key: 'cantidad', align: 'center' },
            { title: 'Precio Un.', dataIndex: 'precio_unitario', render: v => `$${Number(v).toLocaleString()}` },
            { title: 'Subtotal', dataIndex: 'subtotal', render: v => <Text strong>${Number(v).toLocaleString()}</Text> }
        ];
        return <Table columns={columns} dataSource={factura.productos} pagination={false} size="small" rowKey="id" style={{ margin: '10px 20px' }} />;
    };

    // Nivel 2: Facturas (Muestra la caja)
    const expandedCliente = (cliente) => {
        const columns = [
            { title: 'Factura #', dataIndex: 'numero_factura', key: 'factura' },
            { title: 'Fecha', dataIndex: 'fecha', key: 'fecha' },
            { title: 'Turno/Caja ID', dataIndex: 'sesion_caja_id', render: v => <Tag color="blue">Caja #{v}</Tag> },
            { title: 'Total Venta', dataIndex: 'total', render: v => <Text strong style={{ color: '#059669' }}>${Number(v).toLocaleString()}</Text> }
        ];
        return (
            <Table
                columns={columns}
                dataSource={cliente.facturas}
                expandable={{ expandedRowRender: expandedFactura }}
                pagination={false}
                size="small"
                rowKey="id"
            />
        );
    };

    // Nivel 1: Clientes (Principal)
    const colClientes = [
        { title: 'Cliente', dataIndex: 'nombre_cliente', render: text => <><UserOutlined /> {text}</> },
        { title: 'Documento', dataIndex: 'documento_cliente' },
        { title: 'Facturas', dataIndex: 'cantidad_facturas', align: 'center', render: v => <Tag>{v} compras</Tag> },
        { title: 'Total Comprado', dataIndex: 'total_comprado', align: 'right', render: v => <Text strong>${Number(v).toLocaleString()}</Text> }
    ];

    return (
        <Modal
            title={<Title level={4} style={{ margin: 0 }}><ShopOutlined /> Análisis de Ventas: {empleado?.nombre_completo}</Title>}
            open={visible}
            onCancel={onClose}
            width={950}
            footer={null}
            destroyOnClose
        >
            {isLoading ? <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div> : (
                <div style={{ marginTop: '20px' }}>

                    {/* Header Analítico */}
                    <Row gutter={16} style={{ marginBottom: 20 }}>
                        <Col span={12}>
                            <Card size="small" style={{ background: '#f8fafc' }}>
                                <Text type="secondary">Clientes Atendidos</Text>
                                <Title level={3} style={{ margin: 0 }}>{detallesVendedor?.length || 0}</Title>
                            </Card>
                        </Col>
                        <Col span={12} style={{ textAlign: 'right' }}>
                            <Space direction="vertical" size="small">
                                <Text strong>Opciones de Exportación:</Text>
                                <Dropdown menu={{ items: itemsExport }} trigger={['click']}>
                                    <Button type="primary" icon={<DownloadOutlined />} style={{ background: '#22BABB' }}>
                                        Descargar Análisis
                                    </Button>
                                </Dropdown>
                            </Space>
                        </Col>
                    </Row>

                    {/* Tabla Principal */}
                    <Table
                        columns={colClientes}
                        dataSource={detallesVendedor}
                        expandable={{ expandedRowRender: expandedCliente }}
                        rowKey="cliente_id"
                        pagination={false}
                        size="middle"
                        scroll={{ y: '55vh', x: 'max-content' }}
                    />
                </div>
            )}
        </Modal>
    );
};

export default ModalAnalisisVendedor;